/**
 * Couche AEAD : conteneur scellé `version | algo | nonce | ciphertext | tag`.
 *
 * Primitives (aucune ligne de crypto maison ici) :
 *  - XChaCha20-Poly1305 et AES-256-GCM depuis `@noble/ciphers` (audité).
 *  - Nonce issu du CSPRNG, UNIQUE à chaque écriture (`docs/ARCHITECTURE.md` §2).
 *
 * Garanties :
 *  - L'en-tête (`version`, `algo`) est lié en données associées : impossible de
 *    forcer un downgrade d'algorithme sans invalider le tag.
 *  - Le `context` est lié en AAD : un blob d'un usage (protected key) ne peut
 *    être présenté à la place d'un blob d'un autre usage (entrée de coffre).
 *  - Toute altération (ciphertext, tag, nonce, en-tête, contexte, AAD) est
 *    rejetée par `DecryptionFailedError`, sans détail exploitable en oracle.
 */
import { gcm } from '@noble/ciphers/aes.js';
import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
import {
  AEAD_ALGORITHM,
  DEFAULT_AEAD_ALGORITHM,
  NONCE_LENGTH,
  SEALED_BLOB_VERSION,
  SEALED_HEADER_LENGTH,
  SYMMETRIC_KEY_LENGTH,
  TAG_LENGTH,
  type AeadAlgorithmId,
} from './constants.js';
import {
  assertBytes,
  base64ToBytes,
  bytesToBase64,
  concatBytes,
  lengthPrefixed,
  utf8ToBytes,
} from './bytes.js';
import {
  DecryptionFailedError,
  InvalidParameterError,
  MalformedCiphertextError,
  UnsupportedAlgorithmError,
} from './errors.js';
import { randomBytes } from './random.js';
import { assertSecretKey, type SecretKey } from './secret.js';

/** Conteneur scellé sous forme d'octets. */
export type SealedBytes = Uint8Array;

/** Conteneur scellé sous forme base64 (format de stockage et de synchro). */
export type SealedString = string;

export interface SealOptions {
  /**
   * Chaîne de contexte liée en AAD. Obligatoire : elle rend un blob
   * inutilisable hors de son usage. Utiliser `SEAL_CONTEXT.*`.
   */
  readonly context: string;
  /**
   * Données associées supplémentaires (authentifiées, NON chiffrées) — p. ex.
   * l'identifiant d'entrée pour empêcher la permutation de deux entrées.
   */
  readonly associatedData?: Uint8Array | string;
  /** Algorithme d'écriture. Défaut : XChaCha20-Poly1305. */
  readonly algorithm?: AeadAlgorithmId;
}

export type OpenOptions = Omit<SealOptions, 'algorithm'>;

/** En-tête décodé d'un conteneur (données publiques, non secrètes). */
export interface SealedHeader {
  readonly version: number;
  readonly algorithm: AeadAlgorithmId;
  readonly nonce: Uint8Array;
  /** `ciphertext || tag`. */
  readonly body: Uint8Array;
}

function isKnownAlgorithm(value: number): value is AeadAlgorithmId {
  return value === AEAD_ALGORITHM.xchacha20poly1305 || value === AEAD_ALGORITHM.aes256gcm;
}

export function assertAeadAlgorithm(value: unknown): asserts value is AeadAlgorithmId {
  if (typeof value !== 'number' || !isKnownAlgorithm(value)) {
    throw new UnsupportedAlgorithmError(`algorithme AEAD non supporte: ${String(value)}`);
  }
}

/** Longueur de nonce imposée par l'algorithme. */
export function nonceLength(algorithm: AeadAlgorithmId): number {
  assertAeadAlgorithm(algorithm);
  return NONCE_LENGTH[algorithm];
}

/**
 * Construit les données associées canoniques.
 *
 * `AAD = LP(header) || LP(context) || LP(associatedData)` avec
 * `LP(x) = uint32be(len(x)) || x`. Les préfixes de longueur évitent toute
 * ambiguïté de découpage entre champs. Le nonce est authentifié
 * intrinsèquement par la construction AEAD, inutile de le répéter.
 */
function buildAad(header: Uint8Array, options: SealOptions | OpenOptions): Uint8Array {
  if (typeof options?.context !== 'string' || options.context.length === 0) {
    throw new InvalidParameterError('context: chaine non vide obligatoire');
  }
  const extra = options.associatedData ?? new Uint8Array(0);
  const extraBytes = typeof extra === 'string' ? utf8ToBytes(extra) : extra;
  assertBytes(extraBytes, 'associatedData');
  return concatBytes(
    lengthPrefixed(header),
    lengthPrefixed(utf8ToBytes(options.context)),
    lengthPrefixed(extraBytes),
  );
}

function cipherFor(
  algorithm: AeadAlgorithmId,
  key: Uint8Array,
  nonce: Uint8Array,
  aad: Uint8Array,
) {
  switch (algorithm) {
    case AEAD_ALGORITHM.xchacha20poly1305:
      return xchacha20poly1305(key, nonce, aad);
    case AEAD_ALGORITHM.aes256gcm:
      return gcm(key, nonce, aad);
    default:
      throw new UnsupportedAlgorithmError(`algorithme AEAD non supporte: ${String(algorithm)}`);
  }
}

/**
 * Scelle `plaintext` avec un nonce ALEATOIRE FRAIS.
 *
 * Le nonce n'est jamais fourni par l'appelant sur ce chemin : c'est la garantie
 * structurelle de l'invariant « nonce unique par écriture ».
 */
export function seal(key: SecretKey, plaintext: Uint8Array, options: SealOptions): SealedBytes {
  assertSecretKey(key, 'key', SYMMETRIC_KEY_LENGTH);
  assertBytes(plaintext, 'plaintext');
  const algorithm = options.algorithm ?? DEFAULT_AEAD_ALGORITHM;
  assertAeadAlgorithm(algorithm);
  const nonce = randomBytes(nonceLength(algorithm));
  return sealWithNonceUnchecked(key, plaintext, nonce, { ...options, algorithm });
}

/**
 * Chemin de scellement à nonce imposé — INTERNE / TESTS UNIQUEMENT.
 *
 * DANGER : réutiliser un nonce avec la même clé détruit la confidentialité
 * (XOR des plaintexts) et, pour Poly1305/GCM, permet la forge de tags.
 * Réexporté sous le nom `unsafeSealWithNonce` (voir `unsafe.ts`), réservé aux
 * vecteurs de test déterministes.
 */
export function sealWithNonceUnchecked(
  key: SecretKey,
  plaintext: Uint8Array,
  nonce: Uint8Array,
  options: SealOptions,
): SealedBytes {
  assertSecretKey(key, 'key', SYMMETRIC_KEY_LENGTH);
  assertBytes(plaintext, 'plaintext');
  const algorithm = options.algorithm ?? DEFAULT_AEAD_ALGORITHM;
  assertAeadAlgorithm(algorithm);
  assertBytes(nonce, 'nonce', nonceLength(algorithm));

  const header = new Uint8Array([SEALED_BLOB_VERSION, algorithm]);
  const aad = buildAad(header, { ...options, algorithm });
  const body = cipherFor(algorithm, key.reveal(), nonce, aad).encrypt(plaintext);
  return concatBytes(header, nonce, body);
}

/**
 * Décode la structure d'un conteneur. Ne touche PAS à la clé : les erreurs
 * renvoyées ici sont calculables par quiconque détient le blob, donc sans fuite.
 */
export function parseSealed(blob: Uint8Array): SealedHeader {
  assertBytes(blob, 'blob');
  if (blob.length < SEALED_HEADER_LENGTH + 1) {
    throw new MalformedCiphertextError('conteneur tronque: en-tete incomplet');
  }
  const version = blob[0]!;
  if (version !== SEALED_BLOB_VERSION) {
    throw new MalformedCiphertextError(`version de conteneur inconnue: ${version}`);
  }
  const algorithm = blob[1]!;
  if (!isKnownAlgorithm(algorithm)) {
    throw new MalformedCiphertextError(`algorithme de conteneur inconnu: ${algorithm}`);
  }
  const nl = NONCE_LENGTH[algorithm];
  const minimum = SEALED_HEADER_LENGTH + nl + TAG_LENGTH;
  if (blob.length < minimum) {
    throw new MalformedCiphertextError(
      `conteneur tronque: ${minimum} octets minimum, ${blob.length} recus`,
    );
  }
  return {
    version,
    algorithm,
    nonce: blob.subarray(SEALED_HEADER_LENGTH, SEALED_HEADER_LENGTH + nl),
    body: blob.subarray(SEALED_HEADER_LENGTH + nl),
  };
}

/**
 * Ouvre un conteneur scellé.
 *
 * @throws {MalformedCiphertextError} structure invalide (indépendant de la clé)
 * @throws {DecryptionFailedError} authentification refusée — mauvaise clé,
 *   altération, nonce déplacé, contexte ou AAD incorrects. Indistinguables.
 */
export function open(key: SecretKey, blob: Uint8Array, options: OpenOptions): Uint8Array {
  assertSecretKey(key, 'key', SYMMETRIC_KEY_LENGTH);
  const parsed = parseSealed(blob);
  const header = new Uint8Array([parsed.version, parsed.algorithm]);
  const aad = buildAad(header, options);
  try {
    return cipherFor(parsed.algorithm, key.reveal(), parsed.nonce, aad).decrypt(parsed.body);
  } catch {
    // Aucun détail propagé : ne pas fournir d'oracle de déchiffrement.
    throw new DecryptionFailedError();
  }
}

/** Scelle puis encode en base64 (forme stockée / synchronisée). */
export function sealToString(
  key: SecretKey,
  plaintext: Uint8Array,
  options: SealOptions,
): SealedString {
  return bytesToBase64(seal(key, plaintext, options));
}

/** Décode le base64 puis ouvre le conteneur. */
export function openFromString(
  key: SecretKey,
  blob: SealedString,
  options: OpenOptions,
): Uint8Array {
  if (typeof blob !== 'string' || blob.length === 0) {
    throw new MalformedCiphertextError('conteneur base64 vide ou absent');
  }
  let bytes: Uint8Array;
  try {
    bytes = base64ToBytes(blob);
  } catch {
    throw new MalformedCiphertextError('conteneur base64 invalide');
  }
  return open(key, bytes, options);
}

/** Lit l'en-tête public d'un conteneur base64 (diagnostic, migrations). */
export function inspectSealedString(blob: SealedString): {
  version: number;
  algorithm: AeadAlgorithmId;
  nonceLength: number;
  ciphertextLength: number;
} {
  let bytes: Uint8Array;
  try {
    bytes = base64ToBytes(blob);
  } catch {
    throw new MalformedCiphertextError('conteneur base64 invalide');
  }
  const parsed = parseSealed(bytes);
  return {
    version: parsed.version,
    algorithm: parsed.algorithm,
    nonceLength: parsed.nonce.length,
    ciphertextLength: parsed.body.length - TAG_LENGTH,
  };
}
